/**
 * ПОЛНЫЙ ПУТЬ: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminQRCodes\AdminQRPromos.js
 * ВЕРСИЯ: 1.0.0
 * ВРЕМЯ СОЗДАНИЯ: 15.11.2025 18:00
 * 
 * ОПИСАНИЕ: Компонент управления промо QR кодами
 * - Список всех промо кодов
 * - Фильтрация по статусу, сроку действия
 * - Создание новых промо кодов
 * - Редактирование и удаление
 * - Статистика использования
 */

import React, { useState, useEffect } from 'react';
import './AdminQRPromos.css';

const AdminQRPromos = () => {
  const [promos, setPromos] = useState([]);
  const [filteredPromos, setFilteredPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  const [newPromo, setNewPromo] = useState({
    title: '',
    description: '',
    discount: 0,
    max_uses: 0,
    expiry_date: '',
    is_active: true
  });

  useEffect(() => {
    fetchPromos();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [promos, searchQuery, statusFilter]);

  const fetchPromos = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('authToken');
      const response = await fetch('https://qr-money.onrender.com/api/admin/qr/promos', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const promoList = data.data || data.promos || data || [];
      setPromos(Array.isArray(promoList) ? promoList : []);
      console.log('✅ Промо коды загружены:', promoList);
    } catch (err) {
      console.error('❌ Ошибка при загрузке промо кодов:', err);
      setError(err.message);
      setPromos(getMockPromos());
    } finally {
      setLoading(false);
    }
  };

  const getMockPromos = () => [
    {
      id: 1,
      title: 'Летняя акция',
      description: 'Скидка 10% на все переводы',
      discount: 10,
      max_uses: 100,
      used_count: 45,
      expiry_date: '2025-12-31T23:59:59Z',
      is_active: true,
      created_at: '2025-11-01T10:00:00Z',
      status: 'active'
    },
    {
      id: 2,
      title: 'Новогодняя скидка',
      description: 'Скидка 15% до конца декабря',
      discount: 15,
      max_uses: 500,
      used_count: 123,
      expiry_date: '2025-12-31T23:59:59Z',
      is_active: true,
      created_at: '2025-11-01T10:00:00Z',
      status: 'active'
    },
    {
      id: 3,
      title: 'Чёрная пятница',
      description: 'Скидка 25% на выбранные услуги',
      discount: 25,
      max_uses: 50,
      used_count: 50,
      expiry_date: '2025-11-29T23:59:59Z',
      is_active: false,
      created_at: '2025-10-01T10:00:00Z',
      status: 'ended'
    }
  ];

  const applyFilters = () => {
    let filtered = promos;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }

    if (searchQuery.trim()) {
      filtered = filtered.filter(p =>
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredPromos(filtered);
  };

  const handleCreatePromo = async (e) => {
    e.preventDefault();

    if (!newPromo.title.trim()) {
      alert('⚠️ Введите название промо');
      return;
    }

    if (newPromo.discount <= 0 || newPromo.discount > 100) {
      alert('⚠️ Скидка должна быть от 1 до 100%');
      return;
    }

    if (!newPromo.expiry_date) {
      alert('⚠️ Выберите дату окончания');
      return;
    }

    try {
      setCreateLoading(true);
      const token = localStorage.getItem('authToken');

      const response = await fetch('https://qr-money.onrender.com/api/admin/qr/promos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newPromo)
      });

      if (response.ok) {
        const data = await response.json();
        setPromos([...promos, data.data || newPromo]);
        setNewPromo({
          title: '',
          description: '',
          discount: 0,
          max_uses: 0,
          expiry_date: '',
          is_active: true
        });
        setShowCreateForm(false);
        alert('✅ Промо код создан');
      } else {
        const errData = await response.json();
        alert(`❌ Ошибка: ${errData.message || 'при создании'}`);
      }
    } catch (err) {
      console.error('❌ Ошибка:', err);
      alert('❌ Ошибка при создании промо кода');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeletePromo = async (promoId) => {
    if (!window.confirm('Вы уверены что хотите удалить этот промо код?')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`https://qr-money.onrender.com/api/admin/qr/promos/${promoId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setPromos(promos.filter(p => p.id !== promoId));
        alert('✅ Промо код удален');
      } else {
        alert('❌ Ошибка при удалении');
      }
    } catch (err) {
      console.error('❌ Ошибка:', err);
      alert('❌ Ошибка при удалении промо кода');
    }
  };

  const getStatusBadge = (status, isActive) => {
    if (!isActive) {
      return { color: 'status-inactive', text: '❌ Отключен' };
    }
    switch (status) {
      case 'active':
        return { color: 'status-active', text: '✅ Активен' };
      case 'ended':
        return { color: 'status-ended', text: '⏰ Истёк' };
      case 'coming':
        return { color: 'status-coming', text: '⏳ Скоро' };
      default:
        return { color: 'status-active', text: '✅ Активен' };
    }
  };

  const getProgressPercent = (used, max) => {
    if (max === 0) return 0;
    return (used / max) * 100;
  };

  if (loading) {
    return (
      <div className="admin-qr-promos-page">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>⏳ Загрузка промо кодов...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-qr-promos-page">
      {/* ЗАГОЛОВОК */}
      <div className="promos-header">
        <h1>🎁 Управление промо кодами</h1>
        <p>Всего промо: {promos.length} | Показано: {filteredPromos.length}</p>
      </div>

      {/* ОШИБКА */}
      {error && (
        <div className="error-message">
          ⚠️ {error} (используются тестовые данные)
        </div>
      )}

      {/* ФИЛЬТРЫ И КНОПКА */}
      <div className="promos-controls">
        <div className="controls-left">
          <input
            type="text"
            className="search-input"
            placeholder="🔍 Поиск по названию или описанию..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">📊 Все статусы</option>
            <option value="active">✅ Активные</option>
            <option value="ended">⏰ Истёкшие</option>
            <option value="coming">⏳ Будущие</option>
          </select>
        </div>

        <button
          className="btn-create"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          ➕ {showCreateForm ? 'Отмена' : 'Создать промо'}
        </button>
      </div>

      {/* ФОРМА СОЗДАНИЯ */}
      {showCreateForm && (
        <div className="create-form">
          <h2>➕ Новый промо код</h2>
          
          <form onSubmit={handleCreatePromo}>
            <div className="form-row">
              <div className="form-group">
                <label>Название *</label>
                <input
                  type="text"
                  value={newPromo.title}
                  onChange={(e) => setNewPromo({ ...newPromo, title: e.target.value })}
                  className="form-input"
                  placeholder="Летняя акция"
                />
              </div>

              <div className="form-group">
                <label>Скидка (%) *</label>
                <input
                  type="number"
                  value={newPromo.discount}
                  onChange={(e) => setNewPromo({ ...newPromo, discount: parseInt(e.target.value) })}
                  className="form-input"
                  min="1"
                  max="100"
                  placeholder="10"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Описание</label>
              <textarea
                value={newPromo.description}
                onChange={(e) => setNewPromo({ ...newPromo, description: e.target.value })}
                className="form-textarea"
                placeholder="Скидка 10% на все переводы"
                rows="3"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Максимум использований</label>
                <input
                  type="number"
                  value={newPromo.max_uses}
                  onChange={(e) => setNewPromo({ ...newPromo, max_uses: parseInt(e.target.value) })}
                  className="form-input"
                  min="0"
                  placeholder="100"
                />
              </div>

              <div className="form-group">
                <label>Дата окончания *</label>
                <input
                  type="datetime-local"
                  value={newPromo.expiry_date}
                  onChange={(e) => setNewPromo({ ...newPromo, expiry_date: e.target.value })}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={newPromo.is_active}
                  onChange={(e) => setNewPromo({ ...newPromo, is_active: e.target.checked })}
                />
                Активен сразу после создания
              </label>
            </div>

            <button type="submit" className="btn-submit" disabled={createLoading}>
              💾 {createLoading ? 'Создание...' : 'Создать'}
            </button>
          </form>
        </div>
      )}

      {/* СПИСОК ПРОМО */}
      <div className="promos-list">
        {filteredPromos.length > 0 ? (
          filteredPromos.map((promo) => {
            const progress = getProgressPercent(promo.used_count, promo.max_uses);
            const badge = getStatusBadge(promo.status, promo.is_active);
            const expiryDate = new Date(promo.expiry_date);
            const isExpired = expiryDate < new Date();

            return (
              <div key={promo.id} className="promo-card">
                <div className="promo-header">
                  <div className="promo-info">
                    <h3>{promo.title}</h3>
                    <p className="promo-desc">{promo.description || 'Нет описания'}</p>
                  </div>
                  <div className="promo-actions">
                    <span className={`status-badge ${badge.color}`}>
                      {badge.text}
                    </span>
                    <button
                      className="btn-delete"
                      onClick={() => handleDeletePromo(promo.id)}
                      title="Удалить"
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                <div className="promo-stats">
                  <div className="stat-item">
                    <span className="stat-label">Скидка</span>
                    <span className="stat-value">{promo.discount}%</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Использован</span>
                    <span className="stat-value">
                      {promo.used_count}
                      {promo.max_uses > 0 ? `/${promo.max_uses}` : ''}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Окончание</span>
                    <span className={`stat-value ${isExpired ? 'expired' : ''}`}>
                      {expiryDate.toLocaleDateString('ru-RU')}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Создан</span>
                    <span className="stat-value">
                      {new Date(promo.created_at).toLocaleDateString('ru-RU')}
                    </span>
                  </div>
                </div>

                {promo.max_uses > 0 && (
                  <div className="progress-section">
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${Math.min(progress, 100)}%` }} />
                    </div>
                    <span className="progress-text">
                      {Math.round(progress)}% использовано
                    </span>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="no-promos">
            <p>❌ Промо коды не найдены</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminQRPromos;
